import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  DeleteOutlined,
  LoadingOutlined,
  CopyOutlined,
  GlobalOutlined,
  PlusOutlined,
  EditOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { servicesService, ordersService, apiService } from '../../services';
import { ServiceEvent, ServiceEventResponse } from '../../services/services.service';
import { ServiceStatusBadge, ConfirmationModal, ServiceEventsList, DetailPageHeader, SecurityRulesTab, ServiceLogo, HeaderActionMenu } from '../../components/shared';
import { getServiceAge } from '../../utils/serviceHelpers';
import { useNotificationContext } from '../../contexts/NotificationContext';
import yaml from 'js-yaml';

// ─── Types ─────────────────────────────────────────────
interface DockerService {
  name: string;
  image: string;
  ports: string[];
  environment: Record<string, string>;
  volumes: string[];
  restart: string;
  depends_on: string[];
  command: string;
}

interface ComposeResponse {
  yaml: string;
  services: DockerService[];
}

interface ContainerStatus {
  name: string;
  state: 'running' | 'restarting' | 'exited' | 'unknown';
}

interface ContainerFormData {
  name: string;
  image: string;
  ports: string;
  environment: { key: string; value: string }[];
  volumes: string;
  restart: string;
  command: string;
}

// ─── Helpers ───────────────────────────────────────────
const mapLevelToEventType = (level?: string): 'info' | 'warning' | 'error' | 'success' => {
  if (!level) return 'info';
  const l = level.toUpperCase();
  if (l === 'SUCCESS') return 'success';
  if (l === 'ERROR') return 'error';
  if (l === 'WARNING') return 'warning';
  return 'info';
};

const mapStackStatusToEventType = (s?: string): 'info' | 'warning' | 'error' | 'success' => {
  if (!s) return 'info';
  const u = s.toUpperCase();
  if (u.includes('COMPLETE') && !u.includes('FAILED')) return 'success';
  if (u.includes('FAILED') || u.includes('ROLLBACK')) return 'error';
  return 'info';
};

const mapServiceEventsToUI = (events: ServiceEventResponse[]): ServiceEvent[] =>
  events.map((e, i) => {
    let timestamp: Date;
    try { timestamp = new Date(e.createdAt); } catch { timestamp = new Date(); }
    const eventType = e.level ? mapLevelToEventType(e.level) : mapStackStatusToEventType(e.stackStatus);
    const message = e.message || 'No message';
    const isKubiyEvent = e.source?.toLowerCase() === 'kubiy';
    const msgLower = message.toLowerCase();
    const isDeletionEvent = msgLower.includes('deleted') || msgLower.includes('eliminado');
    return {
      id: e.PK || e.SK || `event-${i}`,
      timestamp,
      type: eventType,
      message,
      details: e.description || e.statusReason || undefined,
      source: e.source || 'System',
      isKubiyEvent,
      isDeletionEvent,
    };
  });

const isCreatingStatus = (s?: string): boolean => {
  if (!s) return false;
  const n = s.toLowerCase();
  return ['deploying', 'initializing', 'stacking', 'creating'].includes(n);
};

const validateYaml = (text: string): { valid: boolean; error?: string } => {
  if (!text.trim()) return { valid: false, error: 'Compose YAML is required' };
  try {
    const parsed = yaml.load(text);
    if (!parsed || typeof parsed !== 'object') return { valid: false, error: 'YAML must be an object' };
    const services = (parsed as any).services;
    if (!services || typeof services !== 'object' || Object.keys(services).length === 0) {
      return { valid: false, error: 'Must define at least one service under "services:"' };
    }
    for (const [name, cfg] of Object.entries(services)) {
      if (!(cfg as any).image) return { valid: false, error: `Service "${name}" must have an "image"` };
    }
    return { valid: true };
  } catch (e: any) {
    return { valid: false, error: e.message };
  }
};

const emptyFormData = (): ContainerFormData => ({
  name: '',
  image: '',
  ports: '',
  environment: [],
  volumes: '',
  restart: 'unless-stopped',
  command: '',
});

const statusIcon = (state: string) => {
  switch (state) {
    case 'running': return <span className="text-green-500" title="Running">&#x1F7E2;</span>;
    case 'restarting': return <span className="text-yellow-500" title="Restarting">&#x1F7E1;</span>;
    case 'exited': return <span className="text-red-500" title="Exited">&#x1F534;</span>;
    default: return <span className="text-gray-400" title="Unknown">&#x26AA;</span>;
  }
};

// ─── Container Modal ───────────────────────────────────
const ContainerModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ContainerFormData) => Promise<void>;
  initialData?: ContainerFormData;
  title: string;
  submitLabel: string;
  isEditingName?: boolean;
}> = ({ isOpen, onClose, onSubmit, initialData, title, submitLabel, isEditingName }) => {
  const [form, setForm] = useState<ContainerFormData>(initialData || emptyFormData());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(initialData || emptyFormData());
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(form);
    } finally {
      setSubmitting(false);
    }
  };

  const addEnvVar = () => {
    setForm({ ...form, environment: [...form.environment, { key: '', value: '' }] });
  };

  const updateEnvVar = (index: number, field: 'key' | 'value', val: string) => {
    const env = [...form.environment];
    env[index] = { ...env[index], [field]: val };
    setForm({ ...form, environment: env });
  };

  const removeEnvVar = (index: number) => {
    setForm({ ...form, environment: form.environment.filter((_, i) => i !== index) });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        </div>
        <div className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Service Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={isEditingName}
              placeholder="my-service"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-black dark:focus:ring-white disabled:opacity-50"
            />
          </div>

          {/* Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Image *</label>
            <input
              type="text"
              value={form.image}
              onChange={(e) => setForm({ ...form, image: e.target.value })}
              placeholder="e.g. redis:7-alpine, postgres:16, node:20"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
            />
          </div>

          {/* Ports */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ports</label>
            <textarea
              value={form.ports}
              onChange={(e) => setForm({ ...form, ports: e.target.value })}
              placeholder={"80:80\n443:443"}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-black dark:focus:ring-white resize-none font-mono text-sm"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">One per line, format: host:container</p>
          </div>

          {/* Environment */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Environment Variables</label>
              <button
                type="button"
                onClick={addEnvVar}
                className="text-xs text-sky-600 dark:text-sky-400 hover:text-sky-700 font-medium flex items-center gap-1"
              >
                <PlusOutlined /> Add variable
              </button>
            </div>
            {form.environment.length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500">No environment variables defined.</p>
            )}
            <div className="space-y-2">
              {form.environment.map((env, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    type="text"
                    value={env.key}
                    onChange={(e) => updateEnvVar(i, 'key', e.target.value)}
                    placeholder="KEY"
                    className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm outline-none font-mono"
                  />
                  <input
                    type="text"
                    value={env.value}
                    onChange={(e) => updateEnvVar(i, 'value', e.target.value)}
                    placeholder="value"
                    className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm outline-none font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => removeEnvVar(i)}
                    className="px-2 text-red-500 hover:text-red-700"
                  >
                    <DeleteOutlined />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Volumes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Volumes</label>
            <textarea
              value={form.volumes}
              onChange={(e) => setForm({ ...form, volumes: e.target.value })}
              placeholder={"./data:/app/data\ndb_data:/var/lib/mysql"}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-black dark:focus:ring-white resize-none font-mono text-sm"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">One per line</p>
          </div>

          {/* Restart */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Restart Policy</label>
            <select
              value={form.restart}
              onChange={(e) => setForm({ ...form, restart: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
            >
              <option value="">None</option>
              <option value="no">no</option>
              <option value="always">always</option>
              <option value="unless-stopped">unless-stopped</option>
              <option value="on-failure">on-failure</option>
            </select>
          </div>

          {/* Command */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Command</label>
            <input
              type="text"
              value={form.command}
              onChange={(e) => setForm({ ...form, command: e.target.value })}
              placeholder="Optional override command"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !form.name.trim() || !form.image.trim()}
            className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting && <LoadingOutlined className="animate-spin" />}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────
const DockerHostDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error: showError } = useNotificationContext();

  const [service, setService] = useState<any>(null);
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<ServiceEvent[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<'containers' | 'compose' | 'security'>('containers');

  // Container management state
  const [composeData, setComposeData] = useState<ComposeResponse | null>(null);
  const [containerStatuses, setContainerStatuses] = useState<ContainerStatus[]>([]);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingService, setEditingService] = useState<DockerService | null>(null);
  const [deletingService, setDeletingService] = useState<string | null>(null);

  // Compose YAML tab state
  const [editedYaml, setEditedYaml] = useState('');
  const [yamlError, setYamlError] = useState<string | null>(null);
  const [isSavingYaml, setIsSavingYaml] = useState(false);
  const [yamlDirty, setYamlDirty] = useState(false);

  // Help accordion
  const [showHelp, setShowHelp] = useState(false);

  const hasFetchedRef = useRef<string | null>(null);

  // ─── Fetch Data ───────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      if (!id) { setError('No service ID provided'); setLoading(false); return; }
      if (hasFetchedRef.current === id) return;
      hasFetchedRef.current = id;

      try {
        setLoading(true);
        setError(null);

        const svc: any = await servicesService.getById(id);
        const createdAt = svc.createdAt ? (typeof svc.createdAt === 'string' ? new Date(svc.createdAt) : svc.createdAt) : new Date();
        const updatedAt = svc.updatedAt ? (typeof svc.updatedAt === 'string' ? new Date(svc.updatedAt) : svc.updatedAt) : new Date();
        setService({ ...svc, status: svc.status?.toLowerCase(), createdAt, updatedAt });

        if (svc.orderId) {
          try {
            const orderData = await ordersService.getById(svc.orderId);
            setOrder(orderData);
          } catch { /* ignore */ }
        }

        try {
          const eventsData = await servicesService.getEvents(id);
          setEvents(mapServiceEventsToUI(eventsData));
        } catch { /* ignore */ }

        // Load compose data
        try {
          const compose = await apiService.get<ComposeResponse>(`/services/${id}/docker/compose`);
          setComposeData(compose);
          setEditedYaml(compose.yaml);
        } catch { /* compose not ready yet */ }
      } catch (err: any) {
        setError(err.message || 'Error loading service');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // ─── Polling while creating ───────────────────────────
  useEffect(() => {
    if (!id || !service || !isCreatingStatus(service.status)) return;

    const interval = setInterval(async () => {
      try {
        const svc: any = await servicesService.getById(id);
        const status = svc.status?.toLowerCase();
        const createdAt = svc.createdAt ? (typeof svc.createdAt === 'string' ? new Date(svc.createdAt) : svc.createdAt) : new Date();
        const updatedAt = svc.updatedAt ? (typeof svc.updatedAt === 'string' ? new Date(svc.updatedAt) : svc.updatedAt) : new Date();
        setService({ ...svc, status, createdAt, updatedAt });

        if (!isCreatingStatus(status)) {
          clearInterval(interval);
          const eventsData = await servicesService.getEvents(id);
          setEvents(mapServiceEventsToUI(eventsData));
          // Try to load compose now
          try {
            const compose = await apiService.get<ComposeResponse>(`/services/${id}/docker/compose`);
            setComposeData(compose);
            setEditedYaml(compose.yaml);
          } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    }, 5000);

    return () => clearInterval(interval);
  }, [id, service?.status]);

  // Debounced YAML validation for compose tab
  useEffect(() => {
    if (!editedYaml.trim()) { setYamlError(null); return; }
    const timer = setTimeout(() => {
      const result = validateYaml(editedYaml);
      setYamlError(result.valid ? null : result.error || null);
    }, 500);
    return () => clearTimeout(timer);
  }, [editedYaml]);

  // ─── Refresh ──────────────────────────────────────────
  const handleRefresh = async () => {
    if (!id) return;
    try {
      setIsRefreshing(true);
      const svc: any = await servicesService.getById(id);
      const createdAt = svc.createdAt ? (typeof svc.createdAt === 'string' ? new Date(svc.createdAt) : svc.createdAt) : new Date();
      const updatedAt = svc.updatedAt ? (typeof svc.updatedAt === 'string' ? new Date(svc.updatedAt) : svc.updatedAt) : new Date();
      setService({ ...svc, status: svc.status?.toLowerCase(), createdAt, updatedAt });

      const eventsData = await servicesService.getEvents(id);
      setEvents(mapServiceEventsToUI(eventsData));

      try {
        const compose = await apiService.get<ComposeResponse>(`/services/${id}/docker/compose`);
        setComposeData(compose);
        if (!yamlDirty) setEditedYaml(compose.yaml);
      } catch { /* ignore */ }

      success(t('docker.detail.refreshed'));
    } catch (err: any) {
      showError(err.message || 'Error refreshing');
    } finally {
      setIsRefreshing(false);
    }
  };

  // ─── Delete ───────────────────────────────────────────
  const handleDelete = async () => {
    if (!id) return;
    try {
      setIsDeleting(true);
      await servicesService.delete(id);
      success(t('docker.detail.deleted'));
      navigate('/docker-host');
    } catch (err: any) {
      showError(err.message || 'Error deleting service');
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // ─── Copy helper ──────────────────────────────────────
  const copyText = (text: string, label?: string) => {
    navigator.clipboard.writeText(text);
    success(`${label || 'Value'} copied`);
  };

  // ─── Refresh container status ─────────────────────────
  const refreshContainerStatus = async () => {
    if (!id) return;
    try {
      const statuses = await apiService.get<ContainerStatus[]>(`/services/${id}/docker/status`);
      setContainerStatuses(statuses);
    } catch { /* ignore */ }
  };

  // ─── Refresh compose data ────────────────────────────
  const refreshCompose = useCallback(async () => {
    if (!id) return;
    try {
      const compose = await apiService.get<ComposeResponse>(`/services/${id}/docker/compose`);
      setComposeData(compose);
      if (!yamlDirty) setEditedYaml(compose.yaml);
    } catch { /* ignore */ }
  }, [id, yamlDirty]);

  // ─── Add Container ───────────────────────────────────
  const handleAddContainer = async (data: ContainerFormData) => {
    if (!id) return;
    const payload: any = {
      name: data.name.trim(),
      image: data.image.trim(),
      ports: data.ports.split('\n').map(p => p.trim()).filter(Boolean),
      environment: data.environment.reduce((acc, { key, value }) => {
        if (key.trim()) acc[key.trim()] = value;
        return acc;
      }, {} as Record<string, string>),
      volumes: data.volumes.split('\n').map(v => v.trim()).filter(Boolean),
      restart: data.restart || undefined,
      command: data.command.trim() || undefined,
    };

    await apiService.post(`/services/${id}/docker/services`, payload);
    setShowAddModal(false);
    setHasPendingChanges(true);
    await refreshCompose();
    success(t('docker.detail.serviceAdded'));
  };

  // ─── Edit Container ──────────────────────────────────
  const handleEditContainer = async (data: ContainerFormData) => {
    if (!id) return;
    const payload: any = {
      image: data.image.trim(),
      ports: data.ports.split('\n').map(p => p.trim()).filter(Boolean),
      environment: data.environment.reduce((acc, { key, value }) => {
        if (key.trim()) acc[key.trim()] = value;
        return acc;
      }, {} as Record<string, string>),
      volumes: data.volumes.split('\n').map(v => v.trim()).filter(Boolean),
      restart: data.restart || undefined,
      command: data.command.trim() || undefined,
    };

    await apiService.patch(`/services/${id}/docker/services/${data.name}`, payload);
    setEditingService(null);
    setHasPendingChanges(true);
    await refreshCompose();
    success(t('docker.detail.serviceUpdated'));
  };

  // ─── Delete Container ────────────────────────────────
  const handleDeleteContainer = async () => {
    if (!id || !deletingService) return;
    try {
      await apiService.delete(`/services/${id}/docker/services/${deletingService}`);
      setDeletingService(null);
      setHasPendingChanges(true);
      await refreshCompose();
      success(t('docker.detail.serviceRemoved'));
    } catch (err: any) {
      showError(err.message || 'Error removing service');
    }
  };

  // ─── Apply Changes ───────────────────────────────────
  const handleApplyChanges = async () => {
    if (!id) return;
    setIsApplying(true);
    try {
      const result = await apiService.post<{ status: string; containersRunning?: number; stderr?: string }>(`/services/${id}/docker/apply`, {});
      if (result.status === 'SUCCESS') {
        success(t('docker.detail.applySuccess', { count: result.containersRunning || 0 }));
        setHasPendingChanges(false);
        setYamlDirty(false);
        await refreshCompose();
        await refreshContainerStatus();
      } else {
        showError('Apply failed. Check the events tab for details.');
      }
    } catch (err: any) {
      const stderr = err?.response?.data?.stderr || err?.message || 'Unknown error';
      showError(`Failed to apply changes: ${stderr}`);
    } finally {
      setIsApplying(false);
    }
  };

  // ─── Save YAML ───────────────────────────────────────
  const handleSaveYaml = async () => {
    if (!id) return;
    const result = validateYaml(editedYaml);
    if (!result.valid) {
      showError(result.error || 'Invalid YAML');
      return;
    }
    setIsSavingYaml(true);
    try {
      await apiService.put(`/services/${id}/docker/compose`, { yaml: editedYaml });
      setHasPendingChanges(true);
      setYamlDirty(false);
      await refreshCompose();
      success(t('docker.detail.composeSaved'));
    } catch (err: any) {
      showError(err.message || 'Error saving compose');
    } finally {
      setIsSavingYaml(false);
    }
  };

  // ─── Loading / Error ──────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingOutlined className="text-4xl text-sky-600" spin />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="max-w-[1400px] mx-auto px-8 py-6">
        <button onClick={() => navigate('/docker-host')} className="mb-4 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
          <ArrowLeftOutlined /> Back
        </button>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <p className="text-red-800 dark:text-red-200">{error || 'Service not found'}</p>
        </div>
      </div>
    );
  }

  // ─── Derived Data ─────────────────────────────────────
  const dockerConfig = service.metadata?.dockerConfig || {};
  const instanceType = dockerConfig.instanceType || service.metadata?.instanceType || '-';
  const storage = dockerConfig.storage || service.volumeGB || service.metadata?.volumeGB || '-';
  const region = service.region || dockerConfig.region || 'us-east-1';
  const publicIp = service.publicIp || service.metadata?.publicIp;
  const endpointUrlAdmin = service.endpointUrlAdmin;
  const publicDnsNameKubiy = service.publicDnsNameKubiy;
  const fqdn = publicDnsNameKubiy || endpointUrlAdmin;
  const hostPort = dockerConfig.hostPort || 80;
  const healthPath = dockerConfig.healthPath || '/';

  const serviceToFormData = (svc: DockerService): ContainerFormData => ({
    name: svc.name,
    image: svc.image,
    ports: (svc.ports || []).join('\n'),
    environment: Object.entries(svc.environment || {}).map(([key, value]) => ({ key, value })),
    volumes: (svc.volumes || []).join('\n'),
    restart: svc.restart || '',
    command: svc.command || '',
  });

  const getContainerStatus = (name: string): string => {
    const cs = containerStatuses.find(c => c.name === name);
    return cs?.state || 'unknown';
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
      <DetailPageHeader
        backLabel={t('docker.detail.backToList')}
        onBack={() => navigate('/docker-host')}
        title={service.name}
        subtitle={`Docker Host \u2022 ${instanceType} \u2022 ${region}`}
        icon={<ServiceLogo category="DOCKER" />}
        actions={
          <HeaderActionMenu items={[
            { key: 'refresh', label: t('docker.detail.refresh'), icon: <ReloadOutlined className={isRefreshing ? 'animate-spin' : ''} />, onClick: () => handleRefresh(), disabled: isRefreshing },
            { key: 'delete', label: t('docker.detail.delete'), icon: <DeleteOutlined />, onClick: () => setShowDeleteModal(true), danger: true },
          ]} />
        }
      />

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-8 space-y-6">
          {/* Overview Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('docker.detail.overview')}</h2>
              <ServiceStatusBadge status={service.status} size="md" showLabel />
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Service ID</label>
                <div className="flex items-center gap-2">
                  <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white">{service.id}</code>
                  <button onClick={() => copyText(service.id, 'Service ID')} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    <CopyOutlined />
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Instance Type</label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{instanceType}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Storage</label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{storage} GB</p>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Region</label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{region}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">HTTP Port</label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{hostPort}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Health Check</label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{healthPath}</p>
              </div>
              {publicIp && (
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Public IP</label>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white">{publicIp}</code>
                    <button onClick={() => copyText(publicIp, 'IP')} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                      <CopyOutlined />
                    </button>
                  </div>
                </div>
              )}
              {fqdn && (
                <div className="sm:col-span-2">
                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Public URL (HTTPS)</label>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded font-mono text-gray-900 dark:text-white flex-1 truncate">
                      https://{fqdn}
                    </code>
                    <button onClick={() => copyText(`https://${fqdn}`, 'URL')} className="p-1 text-gray-400 hover:text-gray-600"><CopyOutlined /></button>
                    <a href={`https://${fqdn}`} target="_blank" rel="noopener noreferrer" className="px-2 py-1 text-xs font-medium text-white bg-sky-600 hover:bg-sky-700 rounded transition-colors">Open</a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="border-b border-gray-200 dark:border-gray-700 flex gap-1 px-6 overflow-x-auto">
              {(['containers', 'compose', 'security'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab
                      ? 'border-black dark:border-white text-gray-900 dark:text-white'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {tab === 'security' ? 'Seguridad' : t(`docker.tabs.${tab}`)}
                </button>
              ))}
            </div>

            <div className="p-6">
              {/* ─── Containers Tab ───────────────────────── */}
              {activeTab === 'containers' && (
                <div className="space-y-4">
                  {/* Pending changes banner */}
                  {hasPendingChanges && (
                    <div className="flex items-center justify-between bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3">
                      <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                        <WarningOutlined />
                        <span className="text-sm font-medium">{t('docker.detail.pendingChanges')}</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setHasPendingChanges(false); refreshCompose(); }}
                          className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          {t('docker.detail.discard')}
                        </button>
                        <button
                          onClick={handleApplyChanges}
                          disabled={isApplying}
                          className="px-3 py-1.5 text-sm bg-black dark:bg-white text-white dark:text-black rounded font-medium hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1"
                        >
                          {isApplying && <LoadingOutlined className="animate-spin" />}
                          {t('docker.detail.applyChanges')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Apply in progress overlay */}
                  {isApplying && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-center gap-3">
                      <LoadingOutlined className="text-blue-600 animate-spin text-xl" />
                      <div>
                        <p className="text-sm font-medium text-blue-800 dark:text-blue-200">{t('docker.detail.applying')}</p>
                        <p className="text-xs text-blue-700 dark:text-blue-300">{t('docker.detail.applyingDesc')}</p>
                      </div>
                    </div>
                  )}

                  {/* Header with actions */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('docker.detail.containers')}</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={refreshContainerStatus}
                        className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1"
                      >
                        <ReloadOutlined /> {t('docker.detail.refreshStatus')}
                      </button>
                      <button
                        onClick={() => setShowAddModal(true)}
                        disabled={isApplying}
                        className="px-3 py-1.5 text-sm bg-black dark:bg-white text-white dark:text-black rounded font-medium hover:bg-gray-800 dark:hover:bg-gray-200 flex items-center gap-1 disabled:opacity-50"
                      >
                        <PlusOutlined /> {t('docker.detail.addContainer')}
                      </button>
                    </div>
                  </div>

                  {/* Services Table */}
                  {composeData && composeData.services.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-700">
                            <th className="text-left py-3 px-3 font-medium text-gray-500 dark:text-gray-400">Status</th>
                            <th className="text-left py-3 px-3 font-medium text-gray-500 dark:text-gray-400">Service</th>
                            <th className="text-left py-3 px-3 font-medium text-gray-500 dark:text-gray-400">Image</th>
                            <th className="text-left py-3 px-3 font-medium text-gray-500 dark:text-gray-400">Ports</th>
                            <th className="text-left py-3 px-3 font-medium text-gray-500 dark:text-gray-400">Env vars</th>
                            <th className="text-right py-3 px-3 font-medium text-gray-500 dark:text-gray-400">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {composeData.services.map((svc) => (
                            <tr key={svc.name} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                              <td className="py-3 px-3">{statusIcon(getContainerStatus(svc.name))}</td>
                              <td className="py-3 px-3 font-medium text-gray-900 dark:text-white font-mono">{svc.name}</td>
                              <td className="py-3 px-3 text-gray-600 dark:text-gray-400 font-mono">{svc.image}</td>
                              <td className="py-3 px-3 text-gray-600 dark:text-gray-400">
                                {(svc.ports || []).length > 0 ? svc.ports.join(', ') : '\u2014'}
                              </td>
                              <td className="py-3 px-3 text-gray-600 dark:text-gray-400">
                                {Object.keys(svc.environment || {}).length}
                              </td>
                              <td className="py-3 px-3 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => setEditingService(svc)}
                                    disabled={isApplying}
                                    className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center gap-1"
                                  >
                                    <EditOutlined /> Edit
                                  </button>
                                  <button
                                    onClick={() => setDeletingService(svc.name)}
                                    disabled={isApplying}
                                    className="px-2 py-1 text-xs border border-red-300 dark:border-red-700 rounded text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 flex items-center gap-1"
                                  >
                                    <DeleteOutlined /> Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      {isCreatingStatus(service.status) ? 'Service is still being provisioned...' : 'No containers found. Add one to get started.'}
                    </div>
                  )}

                  {/* Help accordion */}
                  <div className="mt-6 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <button
                      onClick={() => setShowHelp(!showHelp)}
                      className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                    >
                      <span>{t('docker.detail.helpTitle')}</span>
                      <span className={`transition-transform ${showHelp ? 'rotate-180' : ''}`}>&#9660;</span>
                    </button>
                    {showHelp && (
                      <div className="px-4 pb-4 space-y-3">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('docker.detail.helpDesc')}
                        </p>
                        <pre className="text-xs bg-gray-100 dark:bg-gray-900 rounded-lg p-4 font-mono text-gray-800 dark:text-gray-200 overflow-x-auto">
{`cd /opt/kubiy-docker
sudo docker compose logs -f        # view logs
sudo docker compose ps             # list containers
sudo docker compose restart        # restart all`}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ─── Compose YAML Tab ────────────────────── */}
              {activeTab === 'security' && (
                <SecurityRulesTab
                  serviceId={id || ''}
                  securityRules={service?.securityRules}
                  securityGroupId={service?.securityGroupId}
                />
              )}

              {activeTab === 'compose' && (
                <div className="space-y-4">
                  {/* Info banner */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-start gap-2">
                    <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 mt-0.5" />
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      {t('docker.detail.advancedWarning')}
                    </p>
                  </div>

                  {/* Pending changes banner */}
                  {hasPendingChanges && (
                    <div className="flex items-center justify-between bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3">
                      <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                        <WarningOutlined />
                        <span className="text-sm font-medium">{t('docker.detail.pendingChanges')}</span>
                      </div>
                      <button
                        onClick={handleApplyChanges}
                        disabled={isApplying}
                        className="px-3 py-1.5 text-sm bg-black dark:bg-white text-white dark:text-black rounded font-medium hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1"
                      >
                        {isApplying && <LoadingOutlined className="animate-spin" />}
                        Apply Changes
                      </button>
                    </div>
                  )}

                  {isApplying && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-center gap-3">
                      <LoadingOutlined className="text-blue-600 animate-spin text-xl" />
                      <div>
                        <p className="text-sm font-medium text-blue-800 dark:text-blue-200">{t('docker.detail.applying')}</p>
                        <p className="text-xs text-blue-700 dark:text-blue-300">{t('docker.detail.applyingDesc')}</p>
                      </div>
                    </div>
                  )}

                  {/* YAML Editor */}
                  <div className="relative border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                    <div className="flex">
                      <div className="select-none bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 text-right px-3 py-3 border-r border-gray-200 dark:border-gray-700 text-xs leading-[20px] font-mono">
                        {(editedYaml || '\n').split('\n').map((_, i) => (
                          <div key={i}>{i + 1}</div>
                        ))}
                      </div>
                      <textarea
                        value={editedYaml}
                        onChange={(e) => { setEditedYaml(e.target.value); setYamlDirty(true); }}
                        disabled={isApplying}
                        spellCheck={false}
                        className="flex-1 px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none resize-y text-[13px] leading-[20px] disabled:opacity-50"
                        style={{
                          fontFamily: "'Fira Code', 'Monaco', 'Courier New', monospace",
                          minHeight: '600px',
                          tabSize: 2,
                        }}
                      />
                    </div>
                  </div>
                  {yamlError && (
                    <p className="text-sm text-red-500 dark:text-red-400 font-mono bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded px-3 py-2">
                      {yamlError}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleSaveYaml}
                      disabled={isSavingYaml || !yamlDirty || !!yamlError || isApplying}
                      className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isSavingYaml && <LoadingOutlined className="animate-spin" />}
                      {t('docker.detail.saveChanges')}
                    </button>
                    <button
                      onClick={() => { setEditedYaml(composeData?.yaml || ''); setYamlDirty(false); }}
                      disabled={!yamlDirty || isApplying}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('docker.detail.reset')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-4 space-y-6">
          {/* Status Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('docker.detail.status')}</h3>
            </div>
            <div className="p-6 space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
                <ServiceStatusBadge status={service.status} />
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Created</span>
                <span className="text-sm text-gray-900 dark:text-white">{getServiceAge(service.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Updated</span>
                <span className="text-sm text-gray-900 dark:text-white">{getServiceAge(service.updatedAt)}</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          {service.status === 'running' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('docker.detail.quickActions')}</h3>
              </div>
              <div className="p-6 space-y-3">
                <button
                  onClick={() => navigate(`/docker-host/terminal/${id}`)}
                  className="w-full px-4 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <CodeOutlined />
                  {t('virtualMachines.detail.connect') || 'Connect Terminal'}
                </button>
                {fqdn && (
                  <a
                    href={`https://${fqdn}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-4 py-2.5 bg-sky-600 text-white rounded-lg font-medium hover:bg-sky-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <GlobalOutlined />
                    Open Service
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Events */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('docker.detail.events')}</h3>
            </div>
            <div className="p-4">
              <ServiceEventsList
                events={events}
                title=""
                emptyMessage="No events available"
                refreshLabel="Refresh"
                onRefresh={handleRefresh}
                isRefreshing={isRefreshing}
                disabled={!id}
                eventsPerPage={5}
              />
            </div>
          </div>

          {/* Order Info */}
          {order && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Order</h3>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Order ID</span>
                  <button onClick={() => navigate(`/orders/${order.id}`)} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                    {order.id.substring(0, 16)}...
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={t('docker.detail.deleteTitle')}
        message={t('docker.detail.deleteMessage', { name: service.name })}
        confirmText={t('docker.detail.delete')}
        cancelText={t('docker.detail.cancel')}
        variant="danger"
        loading={isDeleting}
      />

      {/* Delete Container Modal */}
      <ConfirmationModal
        isOpen={!!deletingService}
        onClose={() => setDeletingService(null)}
        onConfirm={handleDeleteContainer}
        title={t('docker.detail.removeContainer')}
        message={t('docker.detail.removeContainerMessage', { name: deletingService })}
        confirmText={t('docker.detail.delete')}
        cancelText={t('docker.detail.cancel')}
        variant="danger"
      />

      {/* Add Container Modal */}
      <ContainerModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddContainer}
        title={t('docker.detail.addContainer')}
        submitLabel={t('docker.containerModal.add')}
      />

      {/* Edit Container Modal */}
      <ContainerModal
        isOpen={!!editingService}
        onClose={() => setEditingService(null)}
        onSubmit={handleEditContainer}
        initialData={editingService ? serviceToFormData(editingService) : undefined}
        title={t('docker.detail.editContainer', { name: editingService?.name })}
        submitLabel={t('docker.containerModal.save')}
        isEditingName
      />
    </div>
  );
};

export default DockerHostDetail;
